import React, { memo, useCallback, useMemo, useState } from "react";
import { Modal, Progress, Button, message } from "antd";
import "./project-provisioning-modal.css";

const ProjectProvisioningModal = memo(({
  open,
  onClose,
  project,
  onCancelProvision,
}) => {
  const [canceling, setCanceling] = useState(false);

  const percent = useMemo(() => {
    const p = project?.provisionProgress;
    if (typeof p !== "number") return 0;
    return Math.max(0, Math.min(100, p));
  }, [project?.provisionProgress]);

  const isCanceling = project?.provisionStatus === "canceling" || canceling;

  const handleCancel = useCallback(() => {
    if (!project?.id || !onCancelProvision) return;

    Modal.confirm({
      title: "Cancel project cloning?",
      content: "The clone process will be stopped. The incomplete project can be deleted after cancellation.",
      okText: "Cancel cloning",
      cancelText: "Keep waiting",
      okButtonProps: { danger: true },
      async onOk() {
        setCanceling(true);

        try {
          await onCancelProvision(project.id);
          message.success("Project cloning canceled");
        } catch (e) {
          console.error("Failed to cancel project provisioning", e);
          message.error("Failed to cancel project cloning");
          setCanceling(false);
        }
      }
    });
  }, [project?.id, onCancelProvision]);

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      onCancel={onClose}
      destroyOnHidden
      centered
    >
      <div className="pp-prov">
        <Progress
          className="pp-prov__progress"
          type="circle"
          percent={percent}
          size={35}
          strokeWidth={20}
          showInfo={false}
          strokeColor={{
            "0%": "#00000010",
            "100%": "#00000033",
          }}
          railColor="#00000010"
        />

        <div className="pp-prov__title">
          {isCanceling ? "Cancelling project clone" : "Preparing project"}
        </div>

        <div className="pp-prov__text">
          {isCanceling ? (
            <>
              Cancellation was requested.
              <br />
              The process will stop shortly.
            </>
          ) : (
            <>
              We’re currently generating your project.
              <br />
              This may take a little time, so please wait until the setup is fully completed.
            </>
          )}
        </div>

        <div className="pp-prov__actions">
          <Button
            className="pp-prov__cancel"
            danger
            loading={canceling}
            disabled={isCanceling}
            onClick={handleCancel}
          >
            Cancel cloning
          </Button>

          <Button
            className="pp-prov__ok"
            onClick={onClose}
          >
            OK
          </Button>
        </div>
      </div>
    </Modal>
  );
});

export default ProjectProvisioningModal;